import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import logo from "../../../assets/images/logo-georanking.png";
import { fonts } from "../../../styles/theme";
import { acessoPerfilService } from "../../profile-quality/services";
import type { AcessoPublico } from "../../profile-quality/types";
import { Apoio, Card, Etiqueta, Meta, Pagina, cores } from "../../relatorio/styles";
import { IconeCheck, IconeCheckGrande, IconeGoogle, IconeCadeado, IconePin } from "./icones";

const Fundo = styled(Pagina)`
  background: #F7F9FC;
  display: flex;
  flex-direction: column;
`;

const Topo = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 22px 64px 0;
  img {
    height: 32px;
  }
  @media (max-width: 899px) {
    padding: 18px 20px 0;
    img {
      height: 28px;
    }
  }
`;

const Miolo = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 44px 32px 40px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1.25fr 0.9fr;
  gap: 40px;
  align-items: start;
  @media (max-width: 899px) {
    grid-template-columns: 1fr;
    padding: 24px 20px 24px;
    gap: 20px;
  }
`;

const Coluna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
  min-width: 0;
  @media (max-width: 899px) {
    gap: 20px;
  }
`;

const Titulo = styled.h1`
  font-size: 34px;
  line-height: 1.15;
  letter-spacing: -0.025em;
  @media (max-width: 899px) {
    font-size: 26px;
  }
`;

const Lead = styled.p`
  margin: 12px 0 0;
  font-size: 15px;
  line-height: 1.55;
  color: ${cores.ink2};
  max-width: 560px;
  b {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-weight: 600;
  }
`;

const Passos = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 899px) {
    grid-template-columns: 1fr;
  }
`;

const aparecer = keyframes`
  to {
    opacity: 1;
    transform: none;
  }
`;

const Passo = styled.div<{ $fundo: string; $cor: string; $atraso: number }>`
  background: ${({ $fundo }) => $fundo};
  border-radius: 20px;
  padding: 18px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: 0;
  transform: translateY(14px);
  animation: ${aparecer} 0.8s cubic-bezier(0.25, 1, 0.5, 1) ${({ $atraso }) => $atraso}s forwards;
  .numero {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: ${({ $cor }) => $cor};
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.gilroyBold}, sans-serif;
    font-size: 14px;
  }
  .titulo {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 15px;
    line-height: 1.3;
  }
  .texto {
    font-size: 13px;
    line-height: 1.5;
    color: ${cores.ink2};
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
    transform: none;
  }
`;

const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border-radius: 1000px;
  background: ${cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 16px;
  padding: 15px 26px 15px 18px;
  box-shadow: 0 8px 22px rgba(26, 115, 232, 0.28);
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s ease, background 0.3s ease;
  && {
    color: #fff;
  }
  &:hover {
    background: ${cores.blueDark};
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(26, 115, 232, 0.32);
  }
  .g {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  @media (max-width: 899px) {
    width: 100%;
    box-sizing: border-box;
    font-size: 15px;
  }
`;

const Confianca = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 22px;
  flex-wrap: wrap;
  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: ${cores.ink2};
  }
  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
  @media (max-width: 899px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const Linha = styled.div`
  height: 1px;
  background: ${cores.line};
`;

const Avatar = styled.span`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${cores.blueTint};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Rodape = styled.footer`
  padding: 0 64px 28px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${cores.ink4};
  @media (max-width: 899px) {
    padding: 0 20px 24px;
  }
`;

const Opcao = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid ${cores.line};
  background: #fff;
  border-radius: 16px;
  padding: 14px 16px;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:hover {
    border-color: ${cores.blue};
    background: ${cores.blueTint};
  }
  .titulo {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 15px;
  }
`;

const Concluido = styled.section`
  grid-column: 1 / -1;
  max-width: 720px;
  margin: 0 auto;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  text-align: center;
  .grade {
    width: 100%;
    text-align: left;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }
  @media (max-width: 899px) {
    .grade {
      grid-template-columns: 1fr;
    }
  }
`;

const Forte = styled.b`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-weight: 600;
`;

type Etapa = "carregando" | "pronto" | "concluindo" | "erro";

const PASSOS = [
  { n: 1, fundo: cores.blueTint, cor: "#1264C7", titulo: "Entre com o Google", texto: "Use a conta que é dona do perfil no Google. É ela que tem permissão para liberar o acesso." },
  { n: 2, fundo: cores.yellowTint, cor: "#E37400", titulo: "Confirme a permissão", texto: "Você só autoriza a GeoRanking a ver seus perfis. Nada é alterado sem você saber." },
  { n: 3, fundo: cores.greenTint, cor: "#188038", titulo: "Pronto", texto: "Seu perfil se conecta à nossa ponte de dados e iniciamos o impulsionamento." },
];

/** Página pública: o cliente entra com o Google da conta dona do perfil e concede o acesso. */
export const ConcederAcesso = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [busca, setBusca] = useSearchParams();
  const [etapa, setEtapa] = useState<Etapa>("carregando");
  const [dados, setDados] = useState<AcessoPublico | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const tratado = useRef(false);

  useEffect(() => {
    if (!token || tratado.current) return;
    tratado.current = true;
    const code = busca.get("code");
    const rodar = async () => {
      if (code) {
        setEtapa("concluindo");
        setBusca({}, { replace: true });
        const resposta = await acessoPerfilService.concluir(token, code);
        if (resposta.dados) {
          setDados(resposta.dados);
          setEtapa("pronto");
        } else {
          setErro(resposta.erro);
          setEtapa("erro");
        }
        return;
      }
      const resposta = await acessoPerfilService.consultar(token);
      if (resposta.dados) {
        setDados(resposta.dados);
        setEtapa("pronto");
      } else {
        setErro(resposta.erro);
        setEtapa("erro");
      }
    };
    rodar();
  }, [token, busca, setBusca]);

  const escolher = async (locationName: string) => {
    setEtapa("concluindo");
    const resposta = await acessoPerfilService.escolher(token, locationName);
    if (resposta.dados) {
      setDados(resposta.dados);
      setEtapa("pronto");
    } else {
      setErro(resposta.erro);
      setEtapa("erro");
    }
  };

  const lugar = dados?.city ? `${dados.city}${dados.state ? `/${dados.state}` : ""}` : null;
  const concluido = dados?.status === "CONCLUIDO";
  const conviteEnviado = dados?.status === "CONVITE_ENVIADO";
  const escolhendo = dados?.status === "AGUARDANDO_ESCOLHA" && (dados?.locais?.length ?? 0) > 0;
  const mostraFormulario = etapa === "pronto" && dados && !concluido && !conviteEnviado && !dados.expirado;

  const cartaoDoPerfil = dados && (
    <Card style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar>
          <IconePin />
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16, lineHeight: 1.3 }}>{dados.profileName}</div>
          <Meta>{`Perfil da Empresa no Google${lugar ? ` · ${lugar}` : ""}`}</Meta>
        </div>
      </div>
      <Linha />
      <div>
        <Meta style={{ display: "block", marginBottom: 6 }}>Quem vai receber o acesso</Meta>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14, overflowWrap: "anywhere" }}>{dados.managerEmail}</span>
          <Etiqueta $cor="azul">Conta gestora</Etiqueta>
        </div>
        <Meta style={{ display: "block", marginTop: 6 }}>Conta gestora da GeoRanking que cuida do seu perfil.</Meta>
      </div>
      <Linha />
      <Meta>Link válido por 7 dias · pedido feito pela equipe GeoRanking</Meta>
    </Card>
  );

  return (
    <Fundo>
      <Topo>
        <img src={logo} alt="GeoRanking" />
        <Etiqueta $cor="verde" style={{ gap: 6 }}>
          <IconeCadeado /> Conexão segura com o Google
        </Etiqueta>
      </Topo>

      <Miolo>
        {etapa === "carregando" && <Apoio>Abrindo o pedido…</Apoio>}

        {etapa === "concluindo" && (
          <Coluna>
            <Card $fundo={cores.blueTint} aria-busy="true">
              <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16, color: cores.blue }}>Concedendo o acesso no Google…</span>
              <Apoio style={{ margin: "6px 0 0" }}>Estamos criando o convite para a conta gestora e aceitando por ela. Leva alguns segundos.</Apoio>
            </Card>
          </Coluna>
        )}

        {etapa === "erro" && (
          <Coluna>
            <Card $fundo={cores.yellowTint}>
              <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16, color: cores.amber }}>Não deu para abrir este pedido</span>
              <Apoio style={{ margin: "6px 0 0" }}>{erro ?? "Confira o link recebido ou peça um novo para a GeoRanking."}</Apoio>
            </Card>
          </Coluna>
        )}

        {etapa === "pronto" && dados && concluido && (
          <Concluido>
            <IconeCheckGrande />
            <Titulo>Acesso concedido!</Titulo>
            <Lead style={{ textAlign: "center", maxWidth: 520 }}>
              A conta <Forte>{dados.managerEmail}</Forte> agora tem acesso ao perfil <Forte>{dados.profileName}</Forte>. Seu perfil está conectado à nossa ponte de dados e o impulsionamento começa por aqui.
            </Lead>
            <Card $fundo={cores.greenTint} className="grade" style={{ width: "100%" }}>
              <div>
                <Meta>Perfil</Meta>
                <div style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14, marginTop: 4 }}>{dados.profileName}</div>
              </div>
              <div>
                <Meta>Acesso concedido a</Meta>
                <div style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14, marginTop: 4, overflowWrap: "anywhere" }}>{dados.managerEmail}</div>
              </div>
            </Card>
            <Meta>Você pode fechar esta página. Para remover o acesso no futuro, use “Usuários” no seu Perfil da Empresa no Google.</Meta>
          </Concluido>
        )}

        {etapa === "pronto" && dados && conviteEnviado && (
          <Concluido>
            <IconeCheckGrande cor="#1264C7" />
            <Titulo>Convite enviado!</Titulo>
            <Lead style={{ textAlign: "center", maxWidth: 520 }}>
              O convite de acesso para <Forte>{dados.managerEmail}</Forte> foi criado no perfil <Forte>{dados.profileName}</Forte>. A GeoRanking conclui o aceite do nosso lado; você não precisa fazer mais nada.
            </Lead>
            {dados.errorMessage && <Meta>{dados.errorMessage}</Meta>}
          </Concluido>
        )}

        {etapa === "pronto" && dados && dados.expirado && !concluido && !conviteEnviado && (
          <Coluna>
            <Card $fundo={cores.yellowTint}>
              <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16, color: cores.amber }}>Este link venceu</span>
              <Apoio style={{ margin: "6px 0 0" }}>Peça um novo link para a GeoRanking.</Apoio>
            </Card>
          </Coluna>
        )}

        {mostraFormulario && (
          <>
            <Coluna>
              <div>
                <Etiqueta $cor="azul" style={{ marginBottom: 14 }}>Pedido de acesso</Etiqueta>
                <Titulo>Libere o acesso ao seu perfil no Google em 1 minuto</Titulo>
                <Lead>
                  A GeoRanking precisa de acesso ao perfil <Forte>{dados.profileName}</Forte> para cuidar dele. Você libera pelo próprio Google, sem senha e sem instalar nada.
                </Lead>
              </div>

              {escolhendo ? (
                <Card>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16 }}>Qual destes é o perfil?</span>
                  <Apoio style={{ margin: "4px 0 12px" }}>
                    {`A conta ${dados.customerEmail ?? ""} administra estes perfis. Toque no que corresponde a “${dados.profileName}”.`}
                  </Apoio>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {dados.locais.map((local) => (
                      <Opcao key={local.name} type="button" onClick={() => escolher(local.name)}>
                        <span className="titulo">{local.title ?? local.name}</span>
                        {local.address && <Meta>{local.address}</Meta>}
                      </Opcao>
                    ))}
                  </div>
                </Card>
              ) : (
                <>
                  {dados.status === "ERRO" && dados.errorMessage && (
                    <Card $fundo={cores.redTint}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, color: cores.red }}>Não deu certo da última vez</span>
                      <Apoio style={{ margin: "6px 0 0" }}>{dados.errorMessage}</Apoio>
                    </Card>
                  )}
                  <Passos>
                    {PASSOS.map((passo, indice) => (
                      <Passo key={passo.n} $fundo={passo.fundo} $cor={passo.cor} $atraso={0.15 + indice * 0.1}>
                        <span className="numero">{passo.n}</span>
                        <span className="titulo">{passo.titulo}</span>
                        <span className="texto">{passo.texto}</span>
                      </Passo>
                    ))}
                  </Passos>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start" }}>
                    {dados.authUrl && (
                      <Cta href={dados.authUrl}>
                        <span className="g">
                          <IconeGoogle />
                        </span>
                        Entrar com o Google e liberar o acesso
                      </Cta>
                    )}
                    <Confianca>
                      <li>
                        <IconeCheck />
                        <span>Não guardamos sua senha nem seu acesso ao Google.</span>
                      </li>
                      <li>
                        <IconeCheck />
                        <span>A permissão é usada uma única vez, só para criar o convite.</span>
                      </li>
                      <li>
                        <IconeCheck />
                        <span>Você pode remover o acesso quando quiser, no próprio Google.</span>
                      </li>
                    </Confianca>
                  </div>
                </>
              )}
              {dados.customerEmail && (
                <div>
                  <Etiqueta $cor="cinza">{`Você entrou como ${dados.customerEmail}`}</Etiqueta>
                </div>
              )}
            </Coluna>

            <Coluna style={{ gap: 14, paddingTop: 36 }}>
              {cartaoDoPerfil}
              <Card $fundo={cores.grayTint} style={{ border: `1px solid ${cores.line}` }}>
                <div style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14, marginBottom: 8 }}>Por que isso é seguro?</div>
                <Apoio style={{ margin: 0, fontSize: 13 }}>
                  A liberação acontece dentro do Google, usando o mecanismo oficial de convites do Perfil da Empresa. A GeoRanking só recebe o que você aprovar na tela do Google.
                </Apoio>
              </Card>
            </Coluna>
          </>
        )}
      </Miolo>

      <Rodape>
        <span>© GeoRanking · Gestão de Perfil da Empresa no Google</span>
        <span>
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Política de privacidade</a>
          {" · "}
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer">Termos de uso</a>
        </span>
      </Rodape>
    </Fundo>
  );
};
